import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Image } from "@caspeco/casper-ui-library.components.image";
import { Text } from "@caspeco/casper-ui-library.components.text";
import {
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { SubArticle } from "types/article";
import { AspectRatio } from "types/ui";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { Badge } from "@caspeco/casper-ui-library.components.badge";
import Price from "components/price/Price";

interface IArticleComponent {
	article: SubArticle;
	quantity: number;
	onClick: (articleId: string, articleName: string) => void;
}

function ArticleCard({ article, onClick, quantity }: IArticleComponent) {
	const { id, description, name, inHousePriceIncludingVat, takeAwayPriceIncludingVat, imageMap, status } =
		article;
	const { t } = useTranslation();
	const isOutOfStock = status === "OUT_OF_STOCK";

	const articleCardRef = useRef<HTMLDivElement | null>(null);

	const handleArticleClick = () => {
		if (isOutOfStock) {
			return;
		}
		onClick(id, name);
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (isOutOfStock) {
			return;
		}

		if (event.code === "Space" && document.activeElement === articleCardRef?.current) {
			// User has focused this article and pressed spacebar,
			// this should equal a click action on the article card
			handleArticleClick();
		}
	};

	const opacity = isOutOfStock ? "48%" : "100%";

	const renderArticleImage = () => {
		if (imageMap.hasImage(AspectRatio.OneByOne)) {
			return (
				<Image
					src={imageMap.withSizeQuery(AspectRatio.OneByOne, "m")}
					srcSet={imageMap.getScrSet(AspectRatio.OneByOne, "s", "m")}
					alt={description}
					opacity={opacity}
					fallback={
						<Box
							w="5.5rem"
							h="5.5rem"
							minW="5.5rem"
							minH="5.5rem"
							borderRadius={ThemeRadiusVariable.Medium}
							bg={ThemeColorVariable.BackgroundDisabled}
						/>
					}
					h="5.5rem"
					w="5.5rem"
					borderRadius={ThemeRadiusVariable.Medium}
				/>
			);
		}
		return null;
	};

	const renderPriceAndStatus = () => {
		const price = (
			<Price
				id={`articleprice-${id}`}
				inHousePrice={inHousePriceIncludingVat}
				takeAwayPrice={takeAwayPriceIncludingVat}
				fontFamily={ThemeFontVariable.SubHeader}
				opacity={opacity}
				fontSize={{
					base: ThemeFontSizeVariable.Medium,
					md: ThemeFontSizeVariable.Large,
				}}
				fontWeight="medium"
				color={isOutOfStock ? ThemeColorVariable.OnBackgroundSubdued : ThemeColorVariable.Secondary}
			/>
		);

		if (status === "OUT_OF_STOCK") {
			return (
				<Flex gap={ThemeSpaceVariable.Small}>
					<Badge.Error>{t("article_sold_out")}</Badge.Error>
					{price}
				</Flex>
			);
		}

		return price;
	};

	return (
		<div
			role="button"
			className="articlegrid-item"
			ref={articleCardRef}
			onClick={handleArticleClick}
			onKeyDown={onKeyDown}
			tabIndex={isOutOfStock ? -1 : 0}
			style={{ cursor: isOutOfStock ? "not-allowed" : "pointer" }}
			aria-disabled={isOutOfStock}
		>
			<Box
				display="flex"
				as="article"
				w="100%"
				gap={ThemeSpaceVariable.Medium}
				className="articlegrid-item-content"
			>
				<Box display="flex" flexDirection="column" justifyContent="flex-start">
					<Box display="flex" alignItems="center" gap={ThemeSpaceVariable.XSmall}>
						{quantity > 0 && <Badge.Secondary solid>{`${quantity} x`}</Badge.Secondary>}
						<Heading
							as="h2"
							noOfLines={1}
							fontFamily={ThemeFontVariable.SubHeader}
							fontWeight="medium"
							opacity={opacity}
							color={ThemeColorVariable.OnBackgroundHeader}
							fontSize={{
								base: ThemeFontSizeVariable.Large,
								md: ThemeFontSizeVariable.XLarge,
							}}
						>
							{name}
						</Heading>
					</Box>
					<Text
						noOfLines={1}
						whiteSpace="break-spaces"
						opacity={opacity}
						fontSize={{
							base: ThemeFontSizeVariable.Medium,
							md: ThemeFontSizeVariable.Large,
						}}
					>
						{description}
					</Text>
					<Box mt={ThemeSpaceVariable.XSmall}>{renderPriceAndStatus()}</Box>
				</Box>
				{renderArticleImage()}
			</Box>
		</div>
	);
}

export default ArticleCard;
